/**
 * QuickTestButton.tsx
 *
 * Reusable button component for each quick test type.
 * Shows icon, label, description, and disabled state when resources not selected.
 */

import React from 'react';
import type { LucideIcon } from 'lucide-react';
import { AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';

export type QuickTestType = 'network' | 'sms' | 'data' | 'data_performance' | 'iot_mqtt' | 'iperf' | 'speedtest' | 'voice_call' | 'voice_agent' | 'test_like_mobile';

/**
 * Per-test modem-capability requirement (ANY-of), mirroring each quick-test
 * system flow's `allocationCapabilities` in `systemFlowSeed.ts`. An empty list
 * means the test imposes no network-generation requirement on the modem.
 *
 * Used by the Quick Tests page to gray out a test when a forced network type
 * is selected and no modem at the chosen location can satisfy both — i.e. to
 * pre-empt the server's "none of the modems can be both" allocation error.
 * Keep in sync with `apps/frontend-new/functions/src/seed/systemFlowSeed.ts`.
 */
export const QUICK_TEST_ALLOCATION_CAPABILITIES: Record<QuickTestType, string[]> = {
  network: [],
  sms: [],
  data: ['supports4G', 'supports5G'],
  data_performance: ['supports4G', 'supports5G'],
  iot_mqtt: ['supportsLTEM', 'supportsNBIoT'],
  iperf: ['supports4G', 'supports5G'],
  speedtest: ['supports4G', 'supports5G'],
  voice_call: ['supportsVoLTE'],
  voice_agent: ['supportsVoLTE'],
  test_like_mobile: [],
};

export interface QuickTestButtonProps {
  type: QuickTestType;
  icon: LucideIcon;
  label: string;
  description: string;
  /** Resource gate: steps 1/2 incomplete. */
  disabled: boolean;
  /**
   * When set, the test can't run under the current network-type constraint;
   * the button is grayed out and shows this reason. Distinct from `disabled`
   * so the two states render different tooltips/captions.
   */
  incompatibleReason?: string;
  /**
   * Highlight this card with a filled (muted) background so it stands out from
   * the outlined cards around it (e.g. the flagship "Test Like A Mobile"
   * experience). Only takes effect when the card is otherwise enabled.
   */
  featured?: boolean;
  onClick: () => void;
}

export function QuickTestButton({
  type,
  icon: Icon,
  label,
  description,
  disabled,
  incompatibleReason,
  featured = false,
  onClick,
}: QuickTestButtonProps) {
  const isIncompatible = !!incompatibleReason;
  const isDisabled = disabled || isIncompatible;
  const isFeatured = featured && !isDisabled;
  return (
    <button
      onClick={onClick}
      disabled={isDisabled}
      data-testid={`quick-test-btn-${type}`}
      className={cn(
        'flex flex-col items-start gap-2 p-4 rounded-lg border-2 transition-all h-full',
        isFeatured
          ? 'bg-muted text-foreground border-border shadow-sm hover:border-primary hover:shadow-lg active:scale-[0.98] cursor-pointer'
          : cn(
              'bg-card text-foreground',
              isDisabled
                ? 'opacity-50 cursor-not-allowed border-border'
                : 'border-border hover:border-primary hover:shadow-lg active:scale-[0.98] cursor-pointer'
            ),
        'group'
      )}
      title={
        incompatibleReason ??
        (disabled ? 'Select cluster/location and eSIM first' : undefined)
      }
    >
      {/* Icon + label row */}
      <div className="flex items-center gap-2.5">
        <div
          className={cn(
            'flex items-center justify-center w-9 h-9 rounded-lg shrink-0 transition-colors',
            isDisabled ? 'bg-muted' : 'bg-primary/10 group-hover:bg-primary/20'
          )}
        >
          <Icon
            className={cn(
              'w-5 h-5',
              isDisabled ? 'text-muted-foreground' : 'text-primary'
            )}
          />
        </div>
        <h3 className="font-semibold text-sm text-foreground text-left">{label}</h3>
      </div>

      {/* Description, or the incompatibility reason when blocked by network type */}
      {isIncompatible ? (
        <p className="flex items-start gap-1.5 text-xs text-amber-600 dark:text-amber-500 text-left">
          <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-px" />
          <span>{incompatibleReason}</span>
        </p>
      ) : (
        <p className="text-xs text-muted-foreground line-clamp-2 text-left">{description}</p>
      )}
    </button>
  );
}
